import { PNG_EXPORT_MAX_PIXELS } from './exportCaptureScale.js';
import { shareOrDownloadBlob } from './shareOrDownloadBlob.js';
import {
  A4_PORTRAIT_ASPECT_RATIO,
  FullPageExportStageError,
  type FullPageExportDelivery,
  type FullPageExportStage,
} from './types.js';

type PngExportStage = Extract<FullPageExportStage, 'encode-png' | 'share-download'>;

function throwPngStageError(stage: PngExportStage, message: string, cause?: unknown): never {
  throw new FullPageExportStageError(stage, message, cause !== undefined ? { cause } : undefined);
}

function rethrowAsPngStage(stage: PngExportStage, err: unknown, fallbackMessage: string): never {
  if (err instanceof FullPageExportStageError) {
    throw err;
  }
  const message = err instanceof Error && err.message.trim().length > 0 ? err.message : fallbackMessage;
  throwPngStageError(stage, message, err);
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob));
  });
}

function asCanvasList(
  canvasOrCanvases: HTMLCanvasElement | readonly HTMLCanvasElement[],
): HTMLCanvasElement[] {
  return Array.isArray(canvasOrCanvases) ? [...canvasOrCanvases] : [canvasOrCanvases as HTMLCanvasElement];
}

function sliceCanvasBand(
  source: HTMLCanvasElement,
  sourceY: number,
  bandHeight: number,
): HTMLCanvasElement {
  const slice = document.createElement('canvas');
  slice.width = source.width;
  slice.height = bandHeight;
  const ctx = slice.getContext('2d');
  if (ctx == null) {
    throwPngStageError(
      'encode-png',
      'Full-page PNG export failed: 2D canvas context is unavailable. Retry the export.',
    );
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, slice.width, slice.height);
  ctx.drawImage(source, 0, sourceY, source.width, bandHeight, 0, 0, slice.width, bandHeight);
  return slice;
}

function resolvePngSegmentHeight(canvas: HTMLCanvasElement): number {
  const a4StripHeight = Math.max(1, Math.floor(canvas.width / A4_PORTRAIT_ASPECT_RATIO));
  const budgetStripHeight = Math.max(1, Math.floor(PNG_EXPORT_MAX_PIXELS / Math.max(1, canvas.width)));
  return Math.max(1, Math.min(a4StripHeight, budgetStripHeight));
}

function collectPngParts(canvases: readonly HTMLCanvasElement[]): HTMLCanvasElement[] {
  const parts: HTMLCanvasElement[] = [];
  for (const canvas of canvases) {
    if (canvas.width * canvas.height <= PNG_EXPORT_MAX_PIXELS) {
      parts.push(canvas);
      continue;
    }
    const sourceSegmentHeight = resolvePngSegmentHeight(canvas);
    const segmentCount = Math.max(1, Math.ceil(canvas.height / sourceSegmentHeight));
    for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex += 1) {
      const sourceY = segmentIndex * sourceSegmentHeight;
      if (sourceY >= canvas.height) {
        break;
      }
      const segmentHeight = Math.min(sourceSegmentHeight, canvas.height - sourceY);
      parts.push(sliceCanvasBand(canvas, sourceY, segmentHeight));
    }
  }
  return parts;
}

async function encodePngBlob(canvas: HTMLCanvasElement, failureMessage: string): Promise<Blob> {
  let blob: Blob | null;
  try {
    blob = await canvasToBlob(canvas);
  } catch (err) {
    rethrowAsPngStage('encode-png', err, failureMessage);
  }
  if (blob == null) {
    throwPngStageError('encode-png', failureMessage);
  }
  return blob;
}

async function deliverPngBlob(
  blob: Blob,
  filename: string,
  delivery: FullPageExportDelivery,
): Promise<void> {
  try {
    await shareOrDownloadBlob(
      blob,
      {
        filename,
        mimeType: 'image/png',
      },
      delivery,
    );
  } catch (err) {
    rethrowAsPngStage(
      'share-download',
      err,
      'Full-page PNG export failed while sharing or downloading. Retry the export.',
    );
  }
}

/**
 * Single PNG when one in-budget canvas; otherwise `-part{N}of{M}` strips.
 * Accepts capture bands so a single over-budget canvas is never required.
 * Never redirects to PDF.
 * Encode failures → stage `encode-png`; share/download failures → `share-download`.
 */
export async function exportElementPng(
  canvasOrCanvases: HTMLCanvasElement | readonly HTMLCanvasElement[],
  filenameBase: string,
  delivery: FullPageExportDelivery = 'download',
): Promise<void> {
  let parts: HTMLCanvasElement[];
  try {
    parts = collectPngParts(asCanvasList(canvasOrCanvases));
  } catch (err) {
    rethrowAsPngStage(
      'encode-png',
      err,
      'Full-page PNG export failed while preparing PNG segments. Retry the export.',
    );
  }

  const firstPart = parts[0];
  if (firstPart == null) {
    throwPngStageError(
      'encode-png',
      'Full-page PNG export failed: capture produced no canvases. Retry the export.',
    );
  }

  if (parts.length === 1) {
    const blob = await encodePngBlob(
      firstPart,
      'Full-page PNG export failed: the browser could not encode the image. Retry, or try PDF.',
    );
    await deliverPngBlob(blob, `${filenameBase}.png`, delivery);
    return;
  }

  const segmentCount = parts.length;
  for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex += 1) {
    const segmentCanvas = parts[segmentIndex];
    if (segmentCanvas == null) {
      continue;
    }
    const segmentBlob = await encodePngBlob(
      segmentCanvas,
      `Full-page PNG export failed on part ${segmentIndex + 1} of ${segmentCount}: the browser could not encode the image. Retry, or try PDF.`,
    );
    await deliverPngBlob(
      segmentBlob,
      `${filenameBase}-part${segmentIndex + 1}of${segmentCount}.png`,
      delivery,
    );
  }
}
